// NOTE: This component intentionally has no 'use client' directive.
// It renders server-side when used in page.tsx and client-side when
// imported into FreeCalendarView.tsx. All dependencies (Link, uiFb)
// work in both contexts. Do not add 'use client' or server-only imports.
import Link from 'next/link';
import { uiFb } from '@/lib/i18n';
import type { PillNavLink } from '@/lib/types';

export type { PillNavLink };

interface PillNavProps {
  links: PillNavLink[];
  ariaLabel?: string;
  lang?: string;
}

export function PillNav({ links, ariaLabel, lang = 'en' }: PillNavProps) {
  if (links.length === 0) return null;

  const scrollable = links.length > 5;
  const isNavVariant = !links.some(l => l.active);

  return (
    <div className="pt-4">
      {isNavVariant && (
        <span className="mb-1 block text-label font-medium uppercase tracking-wide text-text-tertiary">
          {uiFb('ui.nav.also_explore', 'Also explore', lang)}
        </span>
      )}
      <nav
        aria-label={ariaLabel ?? uiFb('ui.nav.related_calendars', 'Related calendars', lang)}
        className={scrollable
          ? 'flex snap-x snap-mandatory flex-nowrap gap-1.5 overflow-x-auto px-4 scrollbar-hide sm:flex-wrap sm:justify-start sm:overflow-visible sm:snap-none'
          : 'flex flex-wrap justify-start gap-1.5 px-4'
        }
      >
      {links.map((link) => (
        <Link
          key={link.href}
          href={link.href}
          aria-current={link.active ? 'page' : undefined}
          className={`inline-flex shrink-0 snap-start items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            link.active
              ? 'border-brand bg-brand/10 text-brand'
              : 'border-border-primary bg-surface-card text-text-secondary hover:border-brand hover:text-brand'
          }`}
        >
          {link.icon && <span aria-hidden="true">{link.icon}</span>}
          {link.label}
        </Link>
      ))}
      </nav>
    </div>
  );
}
